import React from 'react';
import { Calendar, MapPin, Phone, Mail, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { useCheerEvent } from '../../hooks/useCheerEvent';

const SECTION_STYLE = {
  background:
    'radial-gradient(ellipse at top, rgba(255, 215, 0, 0.08) 0%, transparent 55%), ' +
    'linear-gradient(180deg, #050505 0%, #1a1300 100%)',
  padding: '4.5rem 1rem',
  borderTop: '1px solid rgba(255, 215, 0, 0.18)',
  borderBottom: '1px solid rgba(255, 215, 0, 0.18)',
};

const CONTAINER_STYLE = { maxWidth: '1100px', margin: '0 auto' };

const HEADER_STYLE = {
  textAlign: 'center',
  marginBottom: '2.5rem',
};

const PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  background: '#FFD700',
  color: '#000',
  padding: '0.35rem 0.95rem',
  borderRadius: '999px',
  fontWeight: 800,
  fontSize: '0.78rem',
  letterSpacing: '1px',
  marginBottom: '1rem',
};

const TITLE_STYLE = {
  fontSize: 'clamp(1.6rem, 3vw, 2.25rem)',
  color: '#FFD700',
  margin: '0 0 0.25rem 0',
  fontWeight: 800,
};

const META_ROW_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '0.5rem 1.5rem',
  color: '#fff',
  fontSize: '0.95rem',
  margin: '0.5rem 0 0 0',
};

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '1.25rem',
  marginBottom: '2rem',
};

const CARD_STYLE = {
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 215, 0, 0.25)',
  borderRadius: '14px',
  padding: '1.5rem',
  position: 'relative',
  transition: 'border-color 0.2s ease, transform 0.2s ease',
};

const STEP_BADGE_STYLE = {
  position: 'absolute',
  top: '-14px',
  left: '20px',
  background: '#FFD700',
  color: '#000',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 800,
  fontSize: '0.95rem',
  boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
};

const CheerJamboreeSection = () => {
  const { event: cheerJamboree } = useCheerEvent();
  return (
  <section
    className="cheer-jamboree-section"
    data-testid="cheer-jamboree-section"
    style={SECTION_STYLE}
  >
    <div style={CONTAINER_STYLE}>
      <div style={HEADER_STYLE}>
        <span style={PILL_STYLE}>
          <Sparkles size={14} /> OFFICIAL PROGRAM · 5 KEY EVENTS
        </span>
        <h2 style={TITLE_STYLE}>{cheerJamboree.title}</h2>
        <p style={{ color: '#f5f5f5', fontSize: '1.05rem', margin: '0 0 0.5rem', fontWeight: 600 }}>
          {cheerJamboree.subtitle}
        </p>
        <p style={{ color: '#FFD700', fontStyle: 'italic', margin: 0 }}>
          {cheerJamboree.tagline}
        </p>
        <div style={META_ROW_STYLE}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <Calendar size={16} style={{ color: '#FFD700' }} />
            <strong>{cheerJamboree.date}</strong>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <MapPin size={16} style={{ color: '#FFD700' }} />
            {cheerJamboree.venue} · {cheerJamboree.city}
          </span>
        </div>
        <p style={{ color: '#d4d4d4', margin: '1.25rem auto 0', maxWidth: '720px', lineHeight: 1.6 }}>
          {cheerJamboree.intro}
        </p>
      </div>

      {/* Five program cards */}
      <div style={GRID_STYLE} data-testid="cheer-jamboree-program-grid">
        {cheerJamboree.program.map((item) => (
          <article
            key={item.step}
            style={CARD_STYLE}
            data-testid={`cheer-program-item-${item.step}`}
          >
            <div style={STEP_BADGE_STYLE}>{item.step}</div>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }} aria-hidden>
              {item.emoji}
            </div>
            <h3 style={{
              color: '#FFD700',
              fontSize: '1.1rem',
              fontWeight: 700,
              margin: '0 0 0.5rem 0',
            }}>
              {item.title}
            </h3>
            <p style={{ color: '#f5f5f5', fontSize: '0.9rem', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
              {item.what}
            </p>
            <p style={{ color: '#cfcfcf', fontSize: '0.88rem', margin: 0, lineHeight: 1.55 }}>
              {item.detail}
            </p>
          </article>
        ))}
      </div>

      {/* Contact CTAs */}
      <div style={{
        background: 'rgba(255, 215, 0, 0.06)',
        border: '1px solid rgba(255, 215, 0, 0.3)',
        borderRadius: '14px',
        padding: '1.5rem',
        textAlign: 'center',
      }}>
        <h3 style={{ color: '#FFD700', margin: '0 0 0.75rem 0', fontSize: '1.1rem', letterSpacing: '0.5px' }}>
          REGISTRATION & INQUIRIES
        </h3>
        <p style={{ color: '#e6e6e6', margin: '0 0 1.25rem 0', fontSize: '0.95rem' }}>
          Squads — contact our cheer coaches below to register or learn more.
        </p>
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center' }}
          data-testid="cheer-jamboree-contacts"
        >
          {cheerJamboree.contacts.map((c) => (
            <a
              key={c.phone}
              href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}
              data-testid={`cheer-contact-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
              style={{ textDecoration: 'none' }}
            >
              <Button className="btn-primary" style={{ fontWeight: 700 }}>
                <Phone size={18} />
                &nbsp;{c.name} — {c.phone}
              </Button>
            </a>
          ))}
          {cheerJamboree.email && (
            <a
              href={`mailto:${cheerJamboree.email}?subject=${encodeURIComponent('Cheer Jamboree — Squad Registration')}`}
              data-testid="cheer-jamboree-email-btn"
              style={{ textDecoration: 'none' }}
            >
              <Button
                className="btn-secondary"
                style={{
                  fontWeight: 700,
                  background: 'transparent',
                  border: '1px solid rgba(255,215,0,0.5)',
                  color: '#FFD700',
                }}
              >
                <Mail size={18} />
                &nbsp;Email Us
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  </section>
  );
};

export default CheerJamboreeSection;
